// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Fleet-level and per-site figures for the Fleet overview: the IT capacity,
// availability and PUE tiles, each site card's capacity and "% · PUE" line,
// and the map hover card's Capacity / Used rows.
//
// There is no data source yet. GA-10 triage (22 Oct 2026) moved the endpoint
// to milestone 2, so every measure here is `null` and the surfaces that read
// it show their "not available" state — never a zero, never an invented
// figure (docs/design/mock-data-philosophy.md). The tiles stay behind
// `fleetOverviewMetricsEnabled` until the source lands.
//
// The contract is settled now so milestone 2 changes only this file: the
// result carries `loading` and `error` like every other query hook here
// (useAsync in useAlarms.ts, and the stubbed useCriticalSystems), so the
// page can tell "still fetching" and "the request failed" from "there is no
// source", and a 5xx never reads as "no data source yet".

/** A figure with its unit, e.g. `{ value: 12.4, unit: 'MW' }`. */
export interface FleetMeasure {
  value: number;
  unit: string;
}

/** One site's figures. */
export interface FleetSiteMetrics {
  /** IT capacity installed at the site. */
  capacity: FleetMeasure | null;
  /** Share of that capacity in use, as a percentage. */
  used: FleetMeasure | null;
  /** Power usage effectiveness (dimensionless). */
  pue: FleetMeasure | null;
}

export interface FleetMetrics {
  /**
   * Share of the fleet's installed IT capacity in use, as a percentage
   * (design: "Fleet IT capacity %", drawn as "72% used"). Not installed MW —
   * that is each site's `capacity`.
   */
  itCapacity: FleetMeasure | null;
  /** Fleet availability as a percentage ("99.98% uptime"). */
  availability: FleetMeasure | null;
  /** Fleet-level PUE, dimensionless ("1.38 avg"): `unit` is ''. */
  pue: FleetMeasure | null;
  /** Keyed by site id. A site with no entry has no figures. */
  sites: Readonly<Record<string, FleetSiteMetrics>>;
}

export const NO_SITE_METRICS: FleetSiteMetrics = { capacity: null, used: null, pue: null };

export interface FleetMetricsResult {
  metrics: FleetMetrics;
  loading: boolean;
  /** Why the last request failed, or null. */
  error: string | null;
}

const NO_METRICS: FleetMetricsResult = {
  metrics: { itCapacity: null, availability: null, pue: null, sites: {} },
  loading: false,
  error: null,
};

/** `12.4 MW`, or the shared "—" when there is nothing to show. */
export function formatMeasure(measure: FleetMeasure | null, noValue = '—'): string {
  return measure ? `${measure.value.toLocaleString()} ${measure.unit}` : noValue;
}

/**
 * The fleet's figures for `siteIds`. Returns nulls today, not loading and
 * not failed — see the header. An empty `siteIds` (a demo scenario) must
 * never cause a request once this fetches, same as useFleetAlarms.
 *
 * A stable object, so callers can put it in hook deps without re-rendering
 * every time.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useFleetMetrics(siteIds: readonly string[]): FleetMetricsResult {
  return NO_METRICS;
}
